import {
  Key,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CheckCircleOutlined,
  CloseOutlined,
  DownloadOutlined,
  EyeOutlined,
  IdcardOutlined,
  LinkOutlined,
  LockOutlined,
  MailOutlined,
  MergeCellsOutlined,
  MoreOutlined,
  PlusOutlined,
  SearchOutlined,
  TeamOutlined,
  UnlockOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Modal, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { TablePaginationConfig } from 'antd/es/table';
import type {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import { useProfile } from '../../profile/components/ProfileContext';
import { CandidatesService } from '../services/candidates.service';
import { CandidateId as ModelCandidate } from '../models/candidates.model';
import { getCandidatePreviewSkills } from '../utils/candidate-preview.utils';
import { UtilService } from '../../../../../core/services/util.service';
import { route } from '../../../../../constants/routes';
import '../../company/components/Company.css';
import './CandidatesTable.css';
import CandidateMergeModal from './CandidateMergeModal';
import { CandidateMergeResult } from '../models/candidate-merge.model';

type CandidateTab = 'active' | 'blocked';

const candidateName = (candidate: ModelCandidate) =>
  `${candidate.firstname || ''} ${candidate.lastname || ''}`.trim() ||
  'Unnamed candidate';

const candidateInitials = (candidate: ModelCandidate) =>
  `${candidate.firstname?.[0] || ''}${
    candidate.lastname?.[0] || ''
  }`.toUpperCase() || 'CA';

const profileUrl = (value: string) => {
  const trimmed = value?.trim();
  if (!trimmed) return '';
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const formattedDate = (value: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const CandidatesTable = forwardRef<{ refresh: () => Promise<void> }>(
  (_, ref) => {
    const { companyId, isAdmin } = useProfile();
    const navigate = useNavigate();
    const location = useLocation();
    const [activeData, setActiveData] = useState<ModelCandidate[]>([]);
    const [blockedData, setBlockedData] = useState<ModelCandidate[]>([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [activeTotal, setActiveTotal] = useState(0);
    const [archivedTotal, setArchivedTotal] = useState(0);
    const [blockedTotal, setBlockedTotal] = useState(0);
    const [activeResultTotal, setActiveResultTotal] = useState(0);
    const [blockedResultTotal, setBlockedResultTotal] = useState(0);
    const [filter, setFilter] = useState<string>();
    const [sort, setSort] = useState<string>();
    const [activeTab, setActiveTab] = useState<CandidateTab>('active');
    const [selectedCandidate, setSelectedCandidate] =
      useState<ModelCandidate | null>(null);
    const [selectedCandidateLoading, setSelectedCandidateLoading] =
      useState(false);
    const [
      selectedCandidateDetailsUnavailable,
      setSelectedCandidateDetailsUnavailable,
    ] = useState(false);
    const [selectedCandidateIds, setSelectedCandidateIds] = useState<Key[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [exporting, setExporting] = useState(false);
    const [mergeOpen, setMergeOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const countsFetchedRef = useRef(false);
    const didInitialLoad = useRef(false);
    const loadingCountRef = useRef(0);
    const previewRequestRef = useRef(0);
    const directoryRequestRef = useRef(0);
    const previewSkills = getCandidatePreviewSkills(
      selectedCandidate?.skills || []
    );
    const hiddenPreviewSkillsCount = Math.max(
      (selectedCandidate?.skills?.length || 0) - previewSkills.length,
      0
    );

    const beginLoading = useCallback(() => {
      loadingCountRef.current += 1;
      setLoading(true);
    }, []);

    const endLoading = useCallback(() => {
      loadingCountRef.current = Math.max(0, loadingCountRef.current - 1);
      if (loadingCountRef.current === 0) setLoading(false);
    }, []);

    const fetchTabData = useCallback(
      async (
        tabType: 'active' | 'archived' | 'blocked',
        fetchDataToo: boolean = true
      ) => {
        if (!companyId) return;

        const filterBy =
          tabType === 'archived'
            ? 'deleted:eq:true'
            : tabType === 'active'
              ? 'deleted:eq:false,blocked:eq:false'
              : 'blocked:eq:true';
        const combinedFilter = filter ? `${filter},${filterBy}` : filterBy;
        const requestId = fetchDataToo ? directoryRequestRef.current + 1 : 0;
        if (fetchDataToo) directoryRequestRef.current = requestId;

        if (fetchDataToo) beginLoading();
        try {
          const params = fetchDataToo
            ? {
                page: pagination.current,
                size: pagination.pageSize,
                sort: sort || '-addedOn',
                filter: combinedFilter,
                ...(searchQuery ? { search: searchQuery } : {}),
              }
            : {
                page: 1,
                size: 1,
                filter: filterBy,
              };
          const result = await CandidatesService.GetCandidates(
            companyId,
            params
          );
          const content = result?.content || [];
          const total = result?.totalElements || 0;

          if (fetchDataToo && requestId !== directoryRequestRef.current) {
            return;
          }

          if (fetchDataToo) {
            if (tabType === 'active') {
              setActiveData(content);
              setActiveResultTotal(total);
              if (!searchQuery && !filter) setActiveTotal(total);
              setSelectedCandidateIds([]);
            } else if (tabType === 'blocked') {
              setBlockedData(content);
              setBlockedResultTotal(total);
              if (!searchQuery && !filter) setBlockedTotal(total);
            }
          } else if (tabType === 'active') {
            setActiveTotal(total);
          } else if (tabType === 'archived') {
            setArchivedTotal(total);
          } else {
            setBlockedTotal(total);
          }
        } catch (error) {
          if (fetchDataToo && requestId !== directoryRequestRef.current) return;
          console.error(`Failed to fetch ${tabType} candidates:`, error);
          UtilService.Alert(
            'Candidates unavailable',
            'error',
            'We could not load this part of the candidate directory. Please try again.'
          );
        } finally {
          if (fetchDataToo) endLoading();
        }
      },
      [
        beginLoading,
        companyId,
        endLoading,
        filter,
        pagination,
        searchQuery,
        sort,
      ]
    );

    const fetchInitialData = useCallback(async () => {
      if (!companyId || countsFetchedRef.current) return;
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', false),
        fetchTabData('blocked', false),
      ]);
      countsFetchedRef.current = true;
    }, [companyId, fetchTabData]);

    useEffect(() => {
      if (companyId && !countsFetchedRef.current && !didInitialLoad.current) {
        void fetchInitialData();
        didInitialLoad.current = true;
      }
    }, [companyId, fetchInitialData]);

    useEffect(() => {
      if (didInitialLoad.current && companyId && countsFetchedRef.current) {
        void fetchTabData(activeTab, true);
      }
    }, [activeTab, companyId, fetchTabData, filter, sort]);

    useEffect(() => {
      const timeout = window.setTimeout(() => {
        setPagination((current) =>
          current.current === 1 ? current : { ...current, current: 1 }
        );
        setSearchQuery(searchTerm.trim());
      }, 300);

      return () => window.clearTimeout(timeout);
    }, [searchTerm]);

    useImperativeHandle(ref, () => ({
      refresh: async () => {
        setPagination({ current: 1, pageSize: 10 });
        setSort(undefined);
        setFilter(undefined);
        setSearchTerm('');
        setSearchQuery('');
        previewRequestRef.current += 1;
        setSelectedCandidate(null);
        setSelectedCandidateLoading(false);
        setSelectedCandidateDetailsUnavailable(false);
        setSelectedCandidateIds([]);
        countsFetchedRef.current = false;
        didInitialLoad.current = false;
        await fetchInitialData();
        didInitialLoad.current = true;
      },
    }));

    const currentData = useMemo(
      () => (activeTab === 'active' ? activeData : blockedData),
      [activeData, activeTab, blockedData]
    );

    const visibleData = currentData;
    const resultTotal =
      activeTab === 'active' ? activeResultTotal : blockedResultTotal;

    const contactableCandidatesOnPage = currentData.filter(
      (candidate) => candidate.email || candidate.phoneNumber
    ).length;

    const fromPath = location.pathname.includes('/company')
      ? route('/company/candidates')
      : route('/candidates');

    const addNew = () => {
      navigate(route('/candidate-profile'), { state: { from: fromPath } });
    };

    const viewCandidate = (candidate: ModelCandidate) => {
      navigate(route(`/candidate-profile/${candidate.id}`), {
        state: {
          candidateName: candidateName(candidate),
          deleted: false,
          from: fromPath,
        },
      });
    };

    const openCandidatePreview = async (candidate: ModelCandidate) => {
      const requestId = previewRequestRef.current + 1;
      previewRequestRef.current = requestId;
      setSelectedCandidate(candidate);
      setSelectedCandidateLoading(true);
      setSelectedCandidateDetailsUnavailable(false);

      try {
        const details = await CandidatesService.GetById(candidate.id);
        if (previewRequestRef.current !== requestId) return;
        setSelectedCandidate({ ...candidate, ...details });
      } catch (error) {
        if (previewRequestRef.current !== requestId) return;
        console.error('Failed to load candidate profile details:', error);
        setSelectedCandidateDetailsUnavailable(true);
      } finally {
        if (previewRequestRef.current === requestId) {
          setSelectedCandidateLoading(false);
        }
      }
    };

    const closeCandidatePreview = () => {
      previewRequestRef.current += 1;
      setSelectedCandidate(null);
      setSelectedCandidateLoading(false);
      setSelectedCandidateDetailsUnavailable(false);
    };

    const blockCandidate = async (candidate: ModelCandidate) => {
      if (!companyId) return;
      try {
        await CandidatesService.Block(candidate, candidate.id, companyId);
        UtilService.Alert(
          'Candidate blacklisted',
          'success',
          `"${candidateName(candidate)}" has been moved to Blacklist.`,
          undefined,
          async () => {
            closeCandidatePreview();
            await Promise.all([
              fetchTabData('blocked', false),
              fetchTabData('active', true),
            ]);
          }
        );
      } catch (error) {
        console.error('Error blacklisting candidate:', error);
        UtilService.Alert(
          'Blacklist unavailable',
          'error',
          'The candidate could not be blacklisted. Please try again.'
        );
      }
    };

    const unblockCandidate = async (candidate: ModelCandidate) => {
      if (!companyId) return;
      try {
        await CandidatesService.Unblock(candidate, candidate.id, companyId);
        UtilService.Alert(
          'Candidate restored',
          'success',
          `"${candidateName(candidate)}" has been restored to Active.`,
          undefined,
          async () => {
            closeCandidatePreview();
            await Promise.all([
              fetchTabData('blocked', true),
              fetchTabData('active', false),
            ]);
          }
        );
      } catch (error) {
        console.error('Error restoring candidate:', error);
        UtilService.Alert(
          'Restore unavailable',
          'error',
          'The candidate could not be restored. Please try again.'
        );
      }
    };

    const confirmBlacklist = (candidate: ModelCandidate) => {
      Modal.confirm({
        title: `Blacklist ${candidateName(candidate)}?`,
        content:
          'The candidate will leave the active talent pool and remain available under Blacklist.',
        okText: 'Blacklist candidate',
        cancelText: 'Cancel',
        okButtonProps: { danger: true },
        onOk: () => blockCandidate(candidate),
      });
    };

    const confirmRestore = (candidate: ModelCandidate) => {
      Modal.confirm({
        title: `Restore ${candidateName(candidate)}?`,
        content: 'The candidate will return to the active talent pool.',
        okText: 'Restore candidate',
        cancelText: 'Cancel',
        onOk: () => unblockCandidate(candidate),
      });
    };

    const downloadCandidateExport = useCallback(
      async (
        candidateIds: string[],
        filename: string,
        successMessage: string
      ) => {
        if (!candidateIds.length) return;
        try {
          setExporting(true);
          const blob = await CandidatesService.ExportSelected(candidateIds);
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = filename;
          document.body.appendChild(anchor);
          anchor.click();
          anchor.remove();
          window.URL.revokeObjectURL(url);
          UtilService.Alert('Export ready', 'success', successMessage);
        } catch (error) {
          console.error('Failed to export candidates:', error);
          UtilService.Alert(
            'Export unavailable',
            'error',
            'The candidate package could not be downloaded. Check your connection and try again.'
          );
        } finally {
          setExporting(false);
        }
      },
      []
    );

    const onChange = (
      paginationConfig: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
      sorter: SorterResult<ModelCandidate> | SorterResult<ModelCandidate>[]
    ) => {
      const filterParts = Object.entries(filters).flatMap(([key, values]) => {
        const value = values?.[0];
        return value ? [`${key}:like:${value}`] : [];
      });
      setFilter(filterParts.length ? filterParts.join(',') : undefined);

      if (!Array.isArray(sorter) && sorter.order) {
        const direction = sorter.order === 'ascend' ? '' : '-';
        setSort(`${direction}${sorter.field}`);
      } else {
        setSort(undefined);
      }

      setPagination({
        current: paginationConfig.current || 1,
        pageSize: paginationConfig.pageSize || 10,
      });
    };

    const columns: ColumnsType<ModelCandidate> = [
      {
        title: 'Candidate',
        dataIndex: 'firstname',
        key: 'firstname',
        sorter: true,
        width: 230,
        render: (_, candidate) => (
          <div className="company-directory__identity-cell">
            <span className="company-directory__avatar" aria-hidden>
              {candidateInitials(candidate)}
            </span>
            <span>
              <strong>{candidateName(candidate)}</strong>
              <small>{candidate.email || 'Email not provided'}</small>
            </span>
          </div>
        ),
      },
      {
        title: 'Contact',
        key: 'contact',
        width: 160,
        render: (_, candidate) => (
          <div className="company-directory__contact-links">
            {candidate.email ? (
              <a href={`mailto:${candidate.email}`}>
                <MailOutlined aria-hidden />
                Email
              </a>
            ) : null}
            {candidate.phoneNumber ? (
              <a
                href={`https://wa.me/${candidate.phoneNumber.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
              >
                <WhatsAppOutlined aria-hidden />
                WhatsApp
              </a>
            ) : (
              <span className="company-directory__muted">No phone</span>
            )}
          </div>
        ),
      },
      {
        title: 'Added',
        dataIndex: 'addedOn',
        key: 'addedOn',
        sorter: true,
        width: 140,
        render: (value: string) => formattedDate(value),
      },
      {
        title: 'Actions',
        key: 'actions',
        align: 'right',
        width: 90,
        render: (_, candidate) => {
          const items = [
            {
              key: 'view',
              icon: <EyeOutlined />,
              label: 'View profile',
            },
            ...(activeTab === 'active'
              ? [
                  {
                    key: 'download',
                    icon: <DownloadOutlined />,
                    label: 'Download package',
                  },
                ]
              : []),
            ...(isAdmin && activeTab === 'active'
              ? [
                  {
                    key: 'blacklist',
                    icon: <LockOutlined />,
                    label: 'Add to blacklist',
                    danger: true,
                  },
                ]
              : []),
            ...(isAdmin && activeTab === 'blocked'
              ? [
                  {
                    key: 'restore',
                    icon: <UnlockOutlined />,
                    label: 'Restore candidate',
                  },
                ]
              : []),
          ];

          return (
            <Dropdown
              trigger={['click']}
              menu={{
                items,
                onClick: ({ key, domEvent }) => {
                  domEvent.stopPropagation();
                  if (key === 'view') viewCandidate(candidate);
                  if (key === 'download') {
                    void downloadCandidateExport(
                      [candidate.id],
                      `candidate-export-${candidate.id}.zip`,
                      'Candidate package downloaded.'
                    );
                  }
                  if (key === 'blacklist') confirmBlacklist(candidate);
                  if (key === 'restore') confirmRestore(candidate);
                },
              }}
            >
              <Button
                type="text"
                className="company-directory__more"
                aria-label={`Actions for ${candidateName(candidate)}`}
                icon={<MoreOutlined />}
                disabled={exporting}
                onClick={(event) => event.stopPropagation()}
              />
            </Dropdown>
          );
        },
      },
    ];

    const rowSelection: TableRowSelection<ModelCandidate> | undefined =
      activeTab === 'active'
        ? {
            selectedRowKeys: selectedCandidateIds,
            onChange: setSelectedCandidateIds,
            preserveSelectedRowKeys: true,
            getCheckboxProps: () => ({ disabled: exporting }),
          }
        : undefined;

    const selectedCandidates = activeData.filter((candidate) =>
      selectedCandidateIds.includes(candidate.id)
    );
    const selectedEmails = selectedCandidates
      .map((candidate) => candidate.email)
      .filter(Boolean)
      .join(',');

    const exportSelected = () =>
      downloadCandidateExport(
        selectedCandidateIds.map(String),
        'candidate-export.zip',
        `${selectedCandidateIds.length} candidate package${
          selectedCandidateIds.length === 1 ? '' : 's'
        } downloaded.`
      );

    const candidateMerged = async (result: CandidateMergeResult) => {
      setMergeOpen(false);
      setSelectedCandidateIds([]);
      closeCandidatePreview();
      await Promise.all([
        fetchTabData('active', true),
        fetchTabData('archived', false),
      ]);
      navigate(route(`/candidate-profile/${result.primaryCandidateId}`), {
        state: { from: fromPath },
      });
    };

    const renderBulkActions = (placement: 'top' | 'bottom') =>
      selectedCandidateIds.length ? (
        <div
          className={`company-directory__bulk candidate-directory__bulk--${placement}`}
          role="group"
          aria-label={
            placement === 'top'
              ? 'Selected candidate actions'
              : 'Selected candidate actions below table'
          }
        >
          {placement === 'top' ? (
            <span
              className="candidate-directory__selection-announcement"
              aria-live="polite"
            >
              {selectedCandidateIds.length} candidate
              {selectedCandidateIds.length === 1 ? '' : 's'} selected
            </span>
          ) : null}
          <strong>
            {selectedCandidateIds.length} candidate
            {selectedCandidateIds.length === 1 ? '' : 's'} selected
          </strong>
          {selectedEmails ? (
            <a href={`mailto:${selectedEmails}`}>
              <MailOutlined aria-hidden />
              Email
            </a>
          ) : null}
          <button
            type="button"
            disabled={exporting}
            onClick={() => void exportSelected()}
          >
            <DownloadOutlined aria-hidden />
            Download ZIP
          </button>
          {selectedCandidates.length === 1 ? (
            <button
              type="button"
              onClick={() => viewCandidate(selectedCandidates[0])}
            >
              <EyeOutlined aria-hidden />
              Open profile
            </button>
          ) : null}
          {isAdmin && selectedCandidateIds.length === 2 ? (
            <button type="button" onClick={() => setMergeOpen(true)}>
              <MergeCellsOutlined aria-hidden />
              Merge candidates
            </button>
          ) : null}
          <button
            className="candidate-directory__bulk-clear"
            type="button"
            onClick={() => setSelectedCandidateIds([])}
          >
            <CloseOutlined aria-hidden />
            Clear
          </button>
        </div>
      ) : null;

    const renderDirectory = (label: string) => (
      <section
        className="company-directory__workspace"
        aria-label={`${label} candidates`}
      >
        <div className="company-directory__toolbar candidate-directory__toolbar">
          <Input
            allowClear
            prefix={<SearchOutlined aria-hidden />}
            aria-label="Search candidates"
            maxLength={200}
            placeholder="Search all candidates by name, email, or phone"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <div className="candidate-directory__toolbar-actions">
            <span
              className="company-directory__result-count"
              aria-live="polite"
            >
              {searchQuery
                ? `${resultTotal} match${resultTotal === 1 ? '' : 'es'}`
                : `${visibleData.length} shown`}
            </span>
            {activeTab === 'active' && !selectedCandidateIds.length ? (
              <Button
                disabled={!activeData.length || exporting}
                onClick={() =>
                  setSelectedCandidateIds(
                    activeData.map((candidate) => candidate.id)
                  )
                }
              >
                Select this page
              </Button>
            ) : null}
          </div>
        </div>

        {renderBulkActions('top')}

        <Table<ModelCandidate>
          className="company-directory__table"
          columns={columns}
          dataSource={visibleData}
          rowKey="id"
          rowSelection={rowSelection}
          loading={loading || exporting}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: resultTotal,
            showSizeChanger: true,
            showTotal: (total) => `${total} candidates`,
            onChange: (current, pageSize) =>
              setPagination({ current, pageSize }),
            onShowSizeChange: (current, pageSize) =>
              setPagination({ current, pageSize }),
          }}
          onChange={onChange}
          onRow={(candidate) => ({
            onClick: () => void openCandidatePreview(candidate),
          })}
          rowClassName={(candidate) =>
            selectedCandidate?.id === candidate.id
              ? 'company-directory__row--previewed'
              : ''
          }
          locale={{
            emptyText: searchQuery
              ? 'No candidates match this search.'
              : 'No candidates are available in this list.',
          }}
          scroll={{ x: 720 }}
        />

        {renderBulkActions('bottom')}
      </section>
    );

    const tabItems = [
      {
        key: 'active',
        label: (
          <span>
            Active <b>{activeTotal}</b>
          </span>
        ),
        children: renderDirectory('Active'),
      },
      {
        key: 'blocked',
        label: (
          <span>
            Blacklist <b>{blockedTotal}</b>
          </span>
        ),
        children: renderDirectory('Blacklisted'),
      },
    ];

    return (
      <div
        className={`company-directory candidate-directory${
          location.pathname.includes('/company')
            ? ''
            : ' candidate-directory--standalone'
        }`}
      >
        <header className="company-directory__header">
          <div>
            <span>Talent pool</span>
            <h2>Candidates</h2>
            <p>Find, evaluate, and engage talent from one place.</p>
          </div>
          <button
            className="company-primary-action"
            type="button"
            onClick={addNew}
          >
            <PlusOutlined aria-hidden />
            Add candidate
          </button>
        </header>

        <section
          className="company-directory__metrics"
          aria-label="Candidate totals"
        >
          <article>
            <span className="company-directory__metric-icon">
              <TeamOutlined aria-hidden />
            </span>
            <div>
              <span>Total candidates</span>
              <strong>{activeTotal + archivedTotal + blockedTotal}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon">
              <CheckCircleOutlined aria-hidden />
            </span>
            <div>
              <span>Active</span>
              <strong>{activeTotal}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon">
              <MailOutlined aria-hidden />
            </span>
            <div>
              <span>Contactable on page</span>
              <strong>{contactableCandidatesOnPage}</strong>
            </div>
          </article>
          <article>
            <span className="company-directory__metric-icon company-directory__metric-icon--warning">
              <LockOutlined aria-hidden />
            </span>
            <div>
              <span>Blacklist</span>
              <strong>{blockedTotal}</strong>
            </div>
          </article>
        </section>

        <div
          className={`company-directory__layout${
            selectedCandidate ? ' company-directory__layout--preview' : ''
          }`}
        >
          <section className="company-directory__panel">
            <Tabs
              activeKey={activeTab}
              items={tabItems}
              onChange={(key) => {
                setActiveTab(key as CandidateTab);
                setPagination((current) => ({ ...current, current: 1 }));
                setSearchTerm('');
                closeCandidatePreview();
                setSelectedCandidateIds([]);
              }}
            />
          </section>

          {selectedCandidate ? (
            <aside
              className="company-directory__preview"
              aria-label="Candidate preview"
            >
              <div className="company-directory__preview-header">
                <strong>Candidate preview</strong>
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  aria-label="Close candidate preview"
                  onClick={closeCandidatePreview}
                />
              </div>
              <div className="company-directory__preview-body">
                <div className="company-directory__preview-identity candidate-directory__preview-identity">
                  <span
                    className="company-directory__preview-avatar"
                    aria-hidden
                  >
                    {candidateInitials(selectedCandidate)}
                  </span>
                  <div>
                    <h3>{candidateName(selectedCandidate)}</h3>
                    <span>
                      {selectedCandidate.rate === null
                        ? 'Not rated yet'
                        : `Rating ${selectedCandidate.rate}/5`}
                    </span>
                    <Tag
                      className={`company-directory__status company-directory__status--${
                        selectedCandidate.blocked ? 'attention' : 'active'
                      }`}
                    >
                      {selectedCandidate.blocked ? 'Blacklisted' : 'Active'}
                    </Tag>
                  </div>
                </div>

                {selectedCandidateLoading ? (
                  <div
                    className="candidate-directory__preview-loading"
                    role="status"
                  >
                    Loading profile details…
                  </div>
                ) : selectedCandidateDetailsUnavailable ? (
                  <div
                    className="candidate-directory__preview-loading"
                    role="status"
                  >
                    Profile details are temporarily unavailable.
                  </div>
                ) : null}

                <section className="company-directory__preview-section">
                  <h4>Contact</h4>
                  <strong>
                    {selectedCandidate.email || 'Email not provided'}
                  </strong>
                  <span>
                    {selectedCandidate.phoneNumber || 'Phone not provided'}
                  </span>
                </section>

                <div className="company-directory__preview-actions">
                  {selectedCandidate.email ? (
                    <a href={`mailto:${selectedCandidate.email}`}>
                      <MailOutlined aria-hidden />
                      Email
                    </a>
                  ) : null}
                  {selectedCandidate.phoneNumber ? (
                    <a
                      href={`https://wa.me/${selectedCandidate.phoneNumber.replace(
                        /\D/g,
                        ''
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <WhatsAppOutlined aria-hidden />
                      WhatsApp
                    </a>
                  ) : null}
                  {!selectedCandidateLoading && selectedCandidate.profileURL ? (
                    <a
                      className="company-directory__preview-action--wide"
                      href={profileUrl(selectedCandidate.profileURL)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <LinkOutlined aria-hidden />
                      Open external profile
                    </a>
                  ) : null}
                </div>

                <section className="company-directory__preview-section">
                  <h4>Top skills</h4>
                  {selectedCandidateLoading ? (
                    <span>Loading skills…</span>
                  ) : selectedCandidateDetailsUnavailable ? (
                    <span>Skills could not be loaded.</span>
                  ) : selectedCandidate.skills?.length ? (
                    <div className="candidate-directory__skills candidate-directory__skills--preview">
                      {previewSkills.map((skill) => (
                        <Tag key={skill.id || skill.skillName}>
                          {skill.skillName}
                        </Tag>
                      ))}
                      {hiddenPreviewSkillsCount > 0 ? (
                        <span>
                          +{hiddenPreviewSkillsCount} more in full profile
                        </span>
                      ) : null}
                    </div>
                  ) : (
                    <span>No skills have been added yet.</span>
                  )}
                </section>

                <section className="company-directory__preview-section">
                  <h4>Profile details</h4>
                  <dl className="company-directory__details">
                    <div>
                      <dt>Added</dt>
                      <dd>{formattedDate(selectedCandidate.addedOn)}</dd>
                    </div>
                    <div>
                      <dt>Files</dt>
                      <dd>{selectedCandidate.files?.length || 0}</dd>
                    </div>
                  </dl>
                </section>
              </div>
              <button
                className="company-primary-action company-directory__preview-primary"
                type="button"
                onClick={() => viewCandidate(selectedCandidate)}
              >
                <IdcardOutlined aria-hidden />
                View full profile
              </button>
            </aside>
          ) : null}
        </div>

        <CandidateMergeModal
          open={mergeOpen}
          candidateIds={selectedCandidateIds.map(String)}
          onCancel={() => setMergeOpen(false)}
          onMerged={(result) => void candidateMerged(result)}
        />
      </div>
    );
  }
);

export default CandidatesTable;
